import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';
import type { JobsQueryParams, CreateJobPayload, WorkersQueryParams, WorkerDrainStatusResponse } from '@/lib/types';
import { toSameOriginPath } from '@/lib/utils';

/**
 * Hard ceiling on a single page walk.
 *
 * The API ends a walk on its own by returning an empty `next`, and a cursor it
 * re-serves unchanged is caught by `seenCursors` below. What is left for this
 * ceiling is a cursor that keeps CHANGING without ever ending — data growing
 * faster than the walk reads it, or links that differ only in a prefix the
 * comparison cannot see — inside a query that also polls.
 */
const MAX_PAGE_WALK = 100;

/**
 * Follow a paginated response's `next` links and return the last envelope,
 * carrying every item collected on the way.
 *
 * Only an empty `next` ends the walk normally. Everything else that can stop it
 * — a cursor already handed out, the `MAX_PAGE_WALK` ceiling, a link this
 * origin refuses, a page that fails to fetch, a body that will not parse, a
 * page whose `items` is missing or is not an array — ends it early and returns
 * the items gathered so far. That uniformity is the point: the first page is
 * already in hand before the walk starts, and neither consumer of these hooks
 * renders a query error state, so throwing would turn a missing tail into an
 * empty panel that reads as "this job has no logs".
 *
 * An `items: []` page is deliberately NOT an ending. An empty page is a
 * legitimate answer, and if the API keeps sending them the cursor and ceiling
 * guards already bound how long that can go on.
 *
 * A truncated walk is not silent to a caller that looks: the returned envelope
 * keeps the last page's `next`, which is non-empty in exactly those cases.
 */
async function walkAllPages<T extends { items: unknown[]; next: string }>(
  firstPage: T,
  apiKey: string,
  subject: string,
): Promise<T> {
  const allItems = [...firstPage.items];
  const seenCursors = new Set<string>();
  let response = firstPage;
  let pagesFetched = 1;

  while (response.next) {
    // A Set, not the previous cursor alone: a cursor that alternates between two
    // values advances on every single step and would otherwise run to the ceiling,
    // appending the same two pages ~50 times.
    if (seenCursors.has(response.next)) {
      console.warn(`Pagination cursor repeated, returning partial ${subject}`);
      break;
    }

    if (pagesFetched >= MAX_PAGE_WALK) {
      console.warn(`Reached the ${MAX_PAGE_WALK}-page walk limit, returning partial ${subject}`);
      break;
    }

    seenCursors.add(response.next);

    let nextPath: string;
    try {
      nextPath = toSameOriginPath(response.next);
    } catch (error) {
      console.warn('Refusing the next page link, returning partial results:', subject, error);
      break;
    }

    let nextPage: T;
    try {
      const nextResponse = await fetch(nextPath, {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!nextResponse.ok) {
        console.warn(`Failed to fetch next page of ${subject}, returning partial results`);
        break;
      }

      nextPage = await nextResponse.json();
    } catch (error) {
      // A network failure, or a 200 whose body is not JSON. The second is not
      // hypothetical: the SPA fallback answers anything the `/v1` rewrite misses
      // with `index.html`, which arrives here as `Unexpected token '<'`.
      console.warn('Could not read the next page, returning partial results:', subject, error);
      break;
    }

    if (!Array.isArray(nextPage.items)) {
      console.warn(`Next page of ${subject} carried no items, returning partial results`);
      break;
    }

    response = nextPage;
    for (const item of nextPage.items) {
      allItems.push(item);
    }
    pagesFetched += 1;
  }

  return { ...response, items: allItems };
}

/**
 * Stop an infinite query whose cursor stopped advancing.
 *
 * Unlike the eager walks above these lists have no page ceiling, so a `next`
 * the query has already followed would keep `hasNextPage` true forever: the
 * scroll sentinel stays in view, and each repeat appends the same page again.
 */
const nextPageParamOnce = (next: string | undefined, allPageParams: (string | undefined)[]): string | undefined =>
  next && !allPageParams.includes(next) ? next : undefined;

/**
 * Fetch one further page of an infinite list.
 *
 * The opposite bargain from `walkAllPages`: this one throws. TanStack keeps the
 * pages already loaded either way, and `JobsPage` reads the query's error, so
 * failing loudly costs nothing here and says more than a silently short list.
 */
async function fetchInfinitePage(pageParam: string, apiKey: string) {
  const response = await fetch(toSameOriginPath(pageParam), {
    headers: {
      'x-api-key': apiKey,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch next page: ${response.status}`);
  }

  return response.json();
}

export const useJobs = (params?: JobsQueryParams, timeRangeFilter?: string | null, enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['jobs', params, timeRangeFilter],
    queryFn: () => api!.getJobs(params, timeRangeFilter),
    refetchInterval: 10_000, // 10s polling
    staleTime: 5_000,
    enabled: !!api && enabled,
  });
};

export const useInfiniteJobs = (params?: JobsQueryParams, timeRangeFilter?: string | null, enabled = true) => {
  const api = useApiClient();

  return useInfiniteQuery({
    queryKey: ['jobs-infinite', params, timeRangeFilter],
    queryFn: async ({ pageParam }) => {
      // A pageParam is the previous page's `next`; its absence means the first page.
      if (pageParam) {
        return fetchInfinitePage(pageParam, api!['config'].apiKey);
      }

      return api!.getJobs(params, timeRangeFilter);
    },
    getNextPageParam: (lastPage, _allPages, _lastPageParam, allPageParams) =>
      nextPageParamOnce(lastPage.next, allPageParams),
    initialPageParam: undefined as string | undefined,
    retry: 1, // Only retry once on error
    staleTime: 5_000,
    enabled: !!api && enabled,
  });
};

export const useJob = (id: string) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['job', id],
    queryFn: () => api!.getJob(id),
    refetchInterval: (query) => {
      const status = query.state.data?.status_name;
      // Poll for all active statuses, stop for terminal statuses
      const activeStatuses = ['pending', 'running', 'retry_pending'];
      return status && activeStatuses.includes(status) ? 10_000 : false;
    },
    enabled: !!api && !!id,
  });
};

export const useJobLogs = (id: string, enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['job-logs', id],
    queryFn: async () => walkAllPages(await api!.getJobLogs(id), api!['config'].apiKey, 'logs'),
    refetchInterval: 30_000, // 30s polling
    enabled: !!api && !!id && enabled,
  });
};

export const useJobAssignments = (id: string) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['job-assignments', id],
    queryFn: async () => walkAllPages(await api!.getJobAssignments(id), api!['config'].apiKey, 'assignments'),
    refetchInterval: 60_000, // 60s polling
    enabled: !!api && !!id,
  });
};

export const useJobCallbackLogs = (id: string, enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['job-callback-logs', id],
    queryFn: async () => {
      const job = await api!.getJobCallbackLogs(id);
      return job.callback_logs || [];
    },
    refetchInterval: 30_000, // 30s polling to catch new retry attempts
    enabled: !!api && !!id && enabled,
  });
};

export const useJobProviderAttempts = (id: string, enabled = true) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['job-provider-attempts', id],
    queryFn: async () => {
      const job = await api!.getJobProviderAttempts(id);
      const attempts = job.provider_attempts || [];
      // Backend has-many embed emits no ORDER BY; sort client-side. `|| 0` guards NaN on malformed timestamps.
      return [...attempts].sort(
        (a, b) => new Date(a.create_ts || 0).getTime() - new Date(b.create_ts || 0).getTime(),
      );
    },
    refetchInterval: 30_000,
    enabled: !!api && !!id && enabled,
  });
};

export const useMetrics = (timeRange?: string) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['metrics', timeRange],
    queryFn: () => api!.getMetrics(timeRange),
    refetchInterval: 10_000, // 10s polling - sync with useJobs for dashboard consistency
    staleTime: 5_000,
    enabled: !!api,
  });
};

// Job types
export const useJobTypes = () => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['job-types'],
    queryFn: () => api!.getJobTypes(),
    staleTime: Infinity, // Job types rarely change
    enabled: !!api,
  });
};

// Create job mutation
export const useCreateJob = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateJobPayload) => api!.createJob(payload),
    onSuccess: () => {
      // Invalidate jobs queries to refetch with new job
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobs-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
    },
  });
};

// Cancel job mutation
export const useCancelJob = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => api!.cancelJob(jobId),
    onSuccess: () => {
      // Invalidate jobs queries to refetch updated status
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobs-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['job'] });
      queryClient.invalidateQueries({ queryKey: ['worker-jobs-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
    },
  });
};

// Retry job mutation
export const useRetryJob = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => api!.retryJob(jobId),
    onSuccess: () => {
      // Invalidate jobs queries to refetch updated status
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobs-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['job'] });
      queryClient.invalidateQueries({ queryKey: ['worker-jobs-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
    },
  });
};

// Worker Jobs - get jobs assigned to a specific worker
export const useInfiniteWorkerJobs = (
  workerId: string,
  params?: JobsQueryParams,
  timeRangeFilter?: string | null,
  enabled = true
) => {
  const api = useApiClient();

  return useInfiniteQuery({
    queryKey: ['worker-jobs-infinite', workerId, params, timeRangeFilter],
    queryFn: async ({ pageParam }) => {
      // A pageParam is the previous page's `next`; its absence means the first page.
      if (pageParam) {
        return fetchInfinitePage(pageParam, api!['config'].apiKey);
      }

      return api!.getWorkerJobs(workerId, params, timeRangeFilter);
    },
    getNextPageParam: (lastPage, _allPages, _lastPageParam, allPageParams) =>
      nextPageParamOnce(lastPage.next, allPageParams),
    initialPageParam: undefined as string | undefined,
    retry: 1,
    staleTime: 5_000,
    enabled: !!api && !!workerId && enabled,
  });
};

// Workers
export const useWorkersEnriched = (params?: WorkersQueryParams, enabled = true) => {
  const api = useApiClient();
  return useQuery({
    queryKey: ['workers-enriched', params],
    queryFn: () => api!.getWorkersEnriched(params),
    refetchInterval: 30_000, // Poll every 30 seconds
    staleTime: 5_000,
    enabled: !!api && enabled,
  });
};

// Cordon (drain) worker mutation. Only the enriched list needs invalidating: drain-status
// queries are keyed on the cordon epoch (drain_requested_ts), so the list refetch re-keys
// them and stale-epoch entries are never served — invalidating them adds nothing.
export const useDrainWorker = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workerId: string) => api!.drainWorker(workerId),
    // Returned so the mutation stays pending until the list refetch lands — the action
    // button must not re-enable while the row still renders the pre-mutation state
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workers-enriched'] }),
  });
};

// Uncordon (undrain) worker mutation — same invalidation rationale as useDrainWorker
export const useUndrainWorker = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workerId: string) => api!.undrainWorker(workerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workers-enriched'] }),
  });
};

// Worker drain status - poll fast while draining, slow once drained.
// Keyed on the cordon epoch (drain_requested_ts): an undrain + re-cordon mints a new
// timestamp and therefore a fresh query, so a drained result from a previous cordon can
// never be served as the safety gate for the current one. Polling settles (to a slow
// cadence, never off — "drained" can regress within an epoch: a worker restart clears the
// ack while preserving drain_requested_ts) only on an error-free response for the SAME
// epoch; errors and epoch mismatches keep the fast cadence.
export const useDrainStatus = (workerId: string, drainRequestedTs: string | null) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['worker-drain-status', workerId, drainRequestedTs],
    queryFn: () => api!.getWorkerDrainStatus(workerId),
    // Consumers only ever see data for the requested epoch — a response for a different one
    // (e.g. a re-cordon the caller's list hasn't observed yet) reads as "still loading"
    select: (data): WorkerDrainStatusResponse | null =>
      data.drain_requested_ts === drainRequestedTs ? data : null,
    refetchInterval: (query) => {
      // query.state.data is the raw cached response — select() does not apply here
      const data = query.state.data;
      const settled =
        !query.state.error && data?.is_drained && data.drain_requested_ts === drainRequestedTs;
      return settled ? 60_000 : 10_000;
    },
    enabled: !!api && !!workerId && drainRequestedTs != null,
  });
};
